import * as React from "react";
import { useEffect, useState } from "react";
import "./vorgangDetail.css";

/**
 * Einklappbare Karte des Detail-Remakes: weißer Karten-Rahmen, Titel links,
 * Chevron OBEN RECHTS (rotierend), Body mit Gold-Trennlinie. Einklapp-Zustand
 * je Karte per `defaultCollapsed` (z. B. Ansprechpartner = eingeklappt,
 * Todos = ausgeklappt).
 */
type Props = {
    title: React.ReactNode;
    /** Kleiner Zähler-Badge neben dem Titel (z. B. Anzahl Todos). */
    count?: number | null;
    /**
     * Zweite, untergeordnete Zahl neben dem Badge — für einen Bestand, der zum
     * Kartenthema gehört, aber nicht dasselbe zählt.
     *
     * Anlass: Die Notizen-Karte zeigte EINE Zahl über Notizen UND Kommentare
     * zusammen. „Notizen 17" konnte also 12 Notizen mit 5 Kommentaren sein —
     * man sah nicht, wie viel davon Antworten waren.
     */
    zusatz?: React.ReactNode;
    defaultCollapsed?: boolean;
    /** false = Karte ohne Einklapp-Verhalten (Übersichtskarte). */
    collapsible?: boolean;
    /** Zusätzliche Kopf-Elemente rechts vor dem Chevron. */
    actions?: React.ReactNode;
    /**
     * Wechselt der Wert auf eine Zahl, klappt die Karte auf (falls zu). Für
     * Sprünge von außen in einen Karteninhalt — der Einklapp-Zustand bleibt
     * ansonsten unangetastet.
     */
    openSignal?: number | null;
    className?: string;
    children: React.ReactNode;
};

const Chevron: React.FC = () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
        <path d="M3 10.5 8 5.5l5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export const CollapsibleCard: React.FC<Props> = ({
    title,
    count,
    zusatz,
    defaultCollapsed = false,
    collapsible = true,
    actions,
    openSignal = null,
    className,
    children,
}) => {
    const [open, setOpen] = useState(!defaultCollapsed);
    const toggle = () => collapsible && setOpen((o) => !o);

    useEffect(() => {
        if (openSignal != null) setOpen(true);
    }, [openSignal]);
    return (
        <section className={`vgd vgd-card ${open ? "open" : ""} ${className ?? ""}`}>
            <header
                className={`vgd-card-h ${collapsible ? "clickable" : ""}`}
                onClick={toggle}
            >
                <div className="vgd-card-title">
                    {title}
                    {count != null && <span className="vgd-card-count">{count}</span>}
                    {zusatz && <span className="vgd-card-zusatz">{zusatz}</span>}
                </div>
                {/* Nur bei offener Karte: der Body wird beim Einklappen aus dem
                    Baum gehängt (s. u.), damit sind Refs auf seine Kinder null —
                    eine Kopf-Aktion, die darüber arbeitet, liefe ins Leere. */}
                {open && actions && <div onClick={(e) => e.stopPropagation()}>{actions}</div>}
                {collapsible && (
                    <button
                        type="button"
                        className="vgd-chev"
                        aria-expanded={open}
                        aria-label={open ? "Einklappen" : "Ausklappen"}
                        onClick={(e) => {
                            e.stopPropagation();
                            toggle();
                        }}
                    >
                        <Chevron />
                    </button>
                )}
            </header>
            {open && <div className="vgd-card-body">{children}</div>}
        </section>
    );
};
